import { __awaiter, __decorate, __param } from "tslib";
import { HTTP_CONFIG, JSONSerializer } from '@genesislcap/foundation-utils';
import { observable, volatile } from '@microsoft/fast-element';
import { DI, optional } from '@microsoft/fast-foundation';
import { BehaviorSubject, concat, finalize, from, interval, switchMap, takeWhile, } from 'rxjs';
import { MetaCache } from '../metadata';
import { Session } from '../session';
import { logger } from '../utils';
import { HttpConnectConfig } from './connect.types';
import { Http } from './http';
import { EventMessageType, MessageBuilder, MessageType, } from './message';
/**
 * The default configuration for connecting to an HTTP service.
 * @public
 */
export const defaultHttpConnectConfig = {
    polling: Object.assign({ POLLING_MAP: {
            EVENT_AMEND_USER: {
                TEMP_FREQUENCY: 500,
                AMOUNT_OF_POLLS: 5,
                QUERIES: ['ALL_USERS'],
            },
        }, POLLING_INTERVAL_MAP: {
            ALL_TRADES: 5000,
            ALL_USERS: 5000,
            ALL_PROCESSES_STATUS: 10000,
        }, POLLING_FREQUENCY: 5000 }, (HTTP_CONFIG && HTTP_CONFIG.length ? JSON.parse(HTTP_CONFIG) : {})),
};
/**
 * The default implementation for the Connect interface (HTTP-only).
 * @public
 */
let DefaultHttpConnect = class DefaultHttpConnect {
    get isConnected() {
        return this._isConnected;
    }
    set isConnected(value) {
        this._isConnected = value;
        this.isConnectedSubject.next(this._isConnected);
    }
    constructor(http, messageBuilder, session, metaCache, serializer, config = defaultHttpConnectConfig) {
        var _a;
        this.http = http;
        this.messageBuilder = messageBuilder;
        this.session = session;
        this.metaCache = metaCache;
        this.serializer = serializer;
        this.config = config;
        this.isWorking = false;
        this._isConnected = false;
        // TODO: Deprecated
        // private isConnectedSubject = new BehaviorSubject(this._isConnected);
        this.isConnectedSubject = new BehaviorSubject(this._isConnected);
        this.isConnected$ = (_a = this.isConnectedSubject) === null || _a === void 0 ? void 0 : _a.asObservable();
        // keep track of events with temporarily increased polling frequency
        this.ongoingTurbo = {};
        /**
         * A map of active messages in "polling" mode.
         * @remarks Equivalent to messaged being streamed in a WebSocket connection.
         * @internal
         */
        this.streams = new Map();
    }
    /**
     * Checks if the host is available by sending a heartbeat ping message and waiting for the response.
     * @internal
     * @returns A Promise that resolves to `true` if the host is available, and `false` otherwise.
     */
    checkHost() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                return this.getMessage({
                    MESSAGE_TYPE: MessageType.HEARTBEAT_PING,
                    SOURCE_REF: 'host check', // doesn't matter
                }).then((response) => {
                    return response.MESSAGE_TYPE === MessageType.HEARTBEAT_PONG;
                });
            }
            catch (e) {
                logger.error(e);
                return false;
            }
        });
    }
    send(message, needsHandling) {
        return this.http.post(DefaultHttpConnect.getUrl(this.host, message), {
            body: DefaultHttpConnect.getBody(message),
            headers: this.messageBuilder.createHTTPHeadersFromMessage(message),
        });
    }
    httpMode() {
        return true;
    }
    setValidSession(valid) {
        logger.warn(`setValidSession() called while in http polling mode.`);
    }
    connect(host) {
        return __awaiter(this, void 0, void 0, function* () {
            this.host = host;
            if (!this.host.endsWith('/')) {
                this.host = this.host.concat('/');
            }
            return this.checkHost().then((connected) => {
                this.isConnected = connected;
                this.session.setSessionStorageItem('hostUrl', this.host);
                return connected;
            });
        });
    }
    disconnect() {
        this.isConnected = false;
        this.streams.clear();
        this.ongoingTurbo = {};
        this.host = undefined;
    }
    snapshot(resourceName, params) {
        return __awaiter(this, void 0, void 0, function* () {
            const snapshotMessage = this.messageBuilder.createDataLogonMessage(resourceName, params);
            logger.debug(`HTTP MODE - ${snapshotMessage.MESSAGE_TYPE} (snapshot) message created -> `, snapshotMessage);
            return this.send(snapshotMessage).then((message) => {
                // snapshot = we only care about the initial payload but since it's a DATASERVER resource (not REQUEST_REPLY) we need to terminate the stream on the server
                this.dataLogoff(snapshotMessage.SOURCE_REF).catch((e) => logger.error(e));
                return message;
            });
        });
    }
    stream(resourceName, onMessage, onError, params) {
        const streamMessage = this.messageBuilder.createDataLogonMessage(resourceName, params);
        logger.debug(`HTTP MODE - ${streamMessage.MESSAGE_TYPE} (stream) message created -> `, streamMessage);
        const SOURCE_REF = streamMessage.SOURCE_REF;
        this.streams.set(SOURCE_REF, {
            SOURCE_REF,
            POLLING_FREQUENCY: new BehaviorSubject(this.config.polling.POLLING_FREQUENCY),
            RESOURCE_NAME: resourceName,
        });
        const interval$ = this.streams.get(SOURCE_REF).POLLING_FREQUENCY;
        const stream = concat(
        // initial DATA_LOGON needs to be a POST
        from(this.send(streamMessage)), 
        // subsequent GET requests using the same SOURCE_REF
        interval$
            .pipe(switchMap((ms) => interval(ms)))
            .pipe(switchMap(() => from(this.getMessage(streamMessage))))).pipe(takeWhile(() => this.streams.has(SOURCE_REF)), // ensure it stops emitting once removed
        finalize(() => {
            logger.debug(`Terminating stream for ${resourceName} - ${SOURCE_REF}`);
            this.dataLogoff(SOURCE_REF);
        }));
        this.streams.get(SOURCE_REF).STREAM = stream;
        return stream;
    }
    getMessage(message) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.http.get(DefaultHttpConnect.getUrl(this.host, message), {
                headers: this.messageBuilder.createHTTPHeadersFromMessage(message),
            });
        });
    }
    getMoreRows(sourceRef) {
        return __awaiter(this, void 0, void 0, function* () {
            const moreRowsMessage = this.messageBuilder.createMoreRowsMessage(sourceRef);
            logger.debug(`HTTP MODE - ${moreRowsMessage.MESSAGE_TYPE} message created -> `, moreRowsMessage);
            return this.send(moreRowsMessage);
        });
    }
    dataLogoff(streamSourceRef) {
        return __awaiter(this, void 0, void 0, function* () {
            const dataLogoffMessage = this.messageBuilder.createDataLogoffMessage(streamSourceRef);
            return this.send(dataLogoffMessage).then((res) => {
                if (this.streams.has(streamSourceRef)) {
                    logger.debug(`Removing stream ${streamSourceRef}`);
                    this.streams.delete(streamSourceRef);
                }
                return res;
            });
        });
    }
    getMetadata(resourceName, useCache = true) {
        return __awaiter(this, void 0, void 0, function* () {
            const metaRequestMessage = this.messageBuilder.createMetaRequestMessage(resourceName);
            logger.debug(`HTTP MODE - ${metaRequestMessage.MESSAGE_TYPE} (metadata) message created -> `, metaRequestMessage);
            // what was top level payload using websockets is now under DETAILS for some reason
            return this.send(metaRequestMessage).then((res) => res.DETAILS);
        });
    }
    getJSONSchema(resourceName) {
        return __awaiter(this, void 0, void 0, function* () {
            const metaRequestMessage = this.messageBuilder.createMetaRequestMessage(resourceName, EventMessageType.JSON_SCHEMA_REQUEST);
            logger.debug(`HTTP MODE! - ${metaRequestMessage.MESSAGE_TYPE} (json schema) message created -> `, metaRequestMessage);
            return this.send(metaRequestMessage);
        });
    }
    request(resourceName, params) {
        const requestMessage = this.messageBuilder.createRequestMessage(resourceName, params);
        logger.debug(`HTTP MODE! - ${requestMessage.MESSAGE_TYPE} (request server) message created -> `, requestMessage);
        return this.send(requestMessage);
    }
    commitEvent(eventName, params) {
        return __awaiter(this, void 0, void 0, function* () {
            const commitMessage = this.messageBuilder.createCommitMessage(eventName, params);
            logger.debug(`HTTP MODE! - ${commitMessage.MESSAGE_TYPE} (commit) message created -> `, commitMessage);
            return this.send(commitMessage).then((reply) => {
                this.maybeTurbo(eventName, reply);
                return reply;
            });
        });
    }
    maybeTurbo(eventName, message) {
        var _a;
        if (!((_a = message === null || message === void 0 ? void 0 : message.MESSAGE_TYPE) === null || _a === void 0 ? void 0 : _a.endsWith('_ACK')))
            return;
        if (this.ongoingTurbo[eventName]) {
            logger.debug(`${eventName} polling frequency is already accelerated.`);
            return;
        }
        const turbo = this.config.polling.POLLING_MAP[eventName];
        if (!((turbo === null || turbo === void 0 ? void 0 : turbo.TEMP_FREQUENCY) && turbo.AMOUNT_OF_POLLS)) {
            return;
        }
        const freq = turbo.TEMP_FREQUENCY;
        const duration = freq * turbo.AMOUNT_OF_POLLS;
        // DEEP COPIES - will be used to override the polling frequency temporarily and then switch back to the original
        const originalPollingIntervalMap = JSON.parse(JSON.stringify(this.config.polling.POLLING_INTERVAL_MAP));
        const tempPollingMap = JSON.parse(JSON.stringify(this.config.polling.POLLING_INTERVAL_MAP));
        for (const q of turbo.QUERIES) {
            tempPollingMap[q] = freq;
            if (!originalPollingIntervalMap[q]) {
                originalPollingIntervalMap[q] = this.config.polling.POLLING_FREQUENCY;
            }
        }
        // temporary override starting now
        logger.debug(`${eventName} TRIGGERED -> ENFORCING TURBO | FREQUENCY=${freq}ms DURATION=${duration}`);
        this.ongoingTurbo[eventName] = true;
        this.updateIntervals({
            polling: {
                POLLING_INTERVAL_MAP: tempPollingMap,
            },
        });
        // schedule restoration of the original polling frequency
        setTimeout(() => {
            logger.debug(`RESTORED ORIGINAL POLLING INTERVALS FOR ${eventName}`);
            this.updateIntervals({
                polling: {
                    POLLING_INTERVAL_MAP: originalPollingIntervalMap,
                },
            });
            this.ongoingTurbo[eventName] = false;
        }, duration);
    }
    updateIntervals(config) {
        const { POLLING_INTERVAL_MAP } = config.polling;
        if (!POLLING_INTERVAL_MAP)
            return;
        this.config.polling.POLLING_INTERVAL_MAP = POLLING_INTERVAL_MAP;
        this.handlePollingIntervalMapUpdate(POLLING_INTERVAL_MAP);
    }
    handlePollingIntervalMapUpdate(pollingIntervalMap) {
        if (!pollingIntervalMap) {
            return;
        }
        for (const resource in pollingIntervalMap) {
            if (!pollingIntervalMap.hasOwnProperty(resource))
                continue;
            const newFreq = pollingIntervalMap[resource];
            this.config.polling.POLLING_INTERVAL_MAP[resource] = newFreq;
            logger.debug(`Setting polling frequency for ${resource} = ${newFreq}`);
            // could have another map by resource name for better efficiency but likely dealing with negligible number of streams
            [...this.streams.values()]
                .filter((s) => s.RESOURCE_NAME === resource)
                .forEach((s) => { var _a; return (_a = s === null || s === void 0 ? void 0 : s.POLLING_FREQUENCY) === null || _a === void 0 ? void 0 : _a.next(newFreq); });
        }
    }
    static getBody(message) {
        return JSON.stringify(message);
    }
    static getUrl(host, message) {
        const type = message.MESSAGE_TYPE;
        let apiEndpoint = message.MESSAGE_TYPE.replace(/_/g, '-').toLowerCase();
        // for DATA_LOGON we go by resource name
        if (type === EventMessageType.DATA_LOGON) {
            apiEndpoint = message.DETAILS.DATASOURCE_NAME.replace(/_/g, '-').toLowerCase();
        }
        return `${host}${apiEndpoint}`;
    }
};
__decorate([
    observable
], DefaultHttpConnect.prototype, "host", void 0);
__decorate([
    observable
], DefaultHttpConnect.prototype, "isWorking", void 0);
__decorate([
    observable
], DefaultHttpConnect.prototype, "_isConnected", void 0);
__decorate([
    volatile
], DefaultHttpConnect.prototype, "isConnected", null);
DefaultHttpConnect = __decorate([
    __param(0, Http),
    __param(1, MessageBuilder),
    __param(2, Session),
    __param(3, MetaCache),
    __param(4, JSONSerializer),
    __param(5, optional(HttpConnectConfig))
], DefaultHttpConnect);
export { DefaultHttpConnect };
/**
 * The DI token for the Connect interface (HTTP-only).
 * @internal
 */
export const HttpConnect = DI.createInterface((x) => x.singleton(DefaultHttpConnect));
